(function () {
  'use strict';

  const STORAGE_KEY = 'lssVotreSouvenirState';
  const STATE_VERSION = 5;
  const CATALOGUE = window.LSSVotreSouvenirCatalogue || null;
  const RULES = window.LSSVotreSouvenirRules || null;
  const PUNCTUATION = ["'", '’', 'ʼ', '-', '–', '—'];

  function createConfigId() {
    const now = new Date();
    const y = String(now.getFullYear());
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    const random = Math.random().toString(36).slice(2, 8).toUpperCase();
    return 'LSS-' + y + m + d + '-' + random;
  }

  function createPieceId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'lss-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  function createDefaultState() {
    return {
      version: STATE_VERSION,
      configId: createConfigId(),
      currentStep: 1,
      prenomOriginal: '',
      prenomTravail: '',
      composition: { slots: [[]] },
      inspiration: {
        mode: '',
        presetId: '',
        ideaText: ''
      },
      accompagnement: {
        wanted: null,
        symbolic: ''
      }
    };
  }

  function createWorkingName(value) {
    return String(value || '')
      .trim()
      .normalize('NFC')
      .toLocaleUpperCase('fr-FR');
  }

  function nameCharacters(value) {
    return Array.from(String(value || ''));
  }

  function isWhitespace(character) {
    return /\s/u.test(character);
  }

  function isPunctuation(character) {
    return PUNCTUATION.includes(character);
  }

  function isStandardLetter(character) {
    return /^[A-Z]$/.test(character);
  }

  function isParticularCharacter(character) {
    return !isStandardLetter(character) && !isWhitespace(character) && !isPunctuation(character);
  }

  function emptySlotsForName(workingName) {
    return Array.from({ length: nameCharacters(workingName).length + 1 }, function () { return []; });
  }

  function normalizePiece(piece) {
    if (!piece || typeof piece !== 'object') return null;
    const kind = piece.kind || piece.type;
    if (!['round', 'flat', 'motif'].includes(kind)) return null;

    return {
      id: typeof piece.id === 'string' ? piece.id : createPieceId(),
      kind: kind,
      material: typeof piece.material === 'string' ? piece.material : 'silicone',
      sizeId: piece.sizeId || null,
      motifId: piece.motifId || null,
      colorId: piece.colorId || null,
      colorName: piece.colorName || '',
      colorHex: piece.colorHex || '#EFE5D8',
      catalogueConfirmed: piece.catalogueConfirmed === true
    };
  }

  function migrateComposition(rawComposition, workingName) {
    if (rawComposition && Array.isArray(rawComposition.slots)) {
      return {
        slots: rawComposition.slots.map(function (slot) {
          return Array.isArray(slot) ? slot.map(normalizePiece).filter(Boolean) : [];
        })
      };
    }

    const slots = emptySlotsForName(workingName);

    if (rawComposition && (Array.isArray(rawComposition.gauche) || Array.isArray(rawComposition.droite))) {
      const left = Array.isArray(rawComposition.gauche) ? rawComposition.gauche.map(normalizePiece).filter(Boolean) : [];
      const right = Array.isArray(rawComposition.droite) ? rawComposition.droite.map(normalizePiece).filter(Boolean) : [];
      slots[0] = left.reverse();
      slots[slots.length - 1] = right;
    }

    return { slots: slots };
  }

  function ensureSlotCount(state) {
    const needed = nameCharacters(state.prenomTravail).length + 1;
    let slots = state.composition && Array.isArray(state.composition.slots)
      ? state.composition.slots.map(function (slot) { return Array.isArray(slot) ? slot : []; })
      : [[]];

    if (slots.length < needed) {
      while (slots.length < needed) slots.push([]);
    } else if (slots.length > needed) {
      const kept = slots.slice(0, Math.max(needed - 1, 0));
      const overflow = slots.slice(Math.max(needed - 1, 0)).flat();
      slots = kept.concat([overflow]);
    }

    if (!slots.length) slots = [[]];
    state.composition = { slots: slots };
    return state;
  }

  function normalizeState(raw) {
    const base = createDefaultState();
    if (!raw || typeof raw !== 'object') return base;

    const prenomOriginal = typeof raw.prenomOriginal === 'string' ? raw.prenomOriginal : '';
    const prenomTravail = typeof raw.prenomTravail === 'string'
      ? raw.prenomTravail
      : createWorkingName(prenomOriginal);

    const inspiration = raw.inspiration && typeof raw.inspiration === 'object' ? raw.inspiration : {};
    const accompagnement = raw.accompagnement && typeof raw.accompagnement === 'object' ? raw.accompagnement : {};

    const state = {
      ...raw,
      version: STATE_VERSION,
      configId: typeof raw.configId === 'string' && raw.configId ? raw.configId : createConfigId(),
      currentStep: [1, 2, 3, 4, 5].includes(Number(raw.currentStep)) ? Number(raw.currentStep) : 1,
      prenomOriginal: prenomOriginal,
      prenomTravail: prenomTravail,
      composition: migrateComposition(raw.composition, prenomTravail),
      inspiration: {
        mode: ['solo', 'preset', 'idea'].includes(inspiration.mode) ? inspiration.mode : '',
        presetId: typeof inspiration.presetId === 'string' ? inspiration.presetId : '',
        ideaText: typeof inspiration.ideaText === 'string' ? inspiration.ideaText.slice(0, 180) : ''
      },
      accompagnement: {
        wanted: accompagnement.wanted === true ? true : accompagnement.wanted === false ? false : null,
        symbolic: typeof accompagnement.symbolic === 'string' ? accompagnement.symbolic.slice(0, 80) : ''
      }
    };

    return ensureSlotCount(state);
  }

  function getStoredState() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? normalizeState(JSON.parse(raw)) : createDefaultState();
    } catch (error) {
      console.warn('[Le Souvenir Simple] Lecture du configurateur impossible.', error);
      return createDefaultState();
    }
  }

  function saveState(state) {
    const normalized = ensureSlotCount(normalizeState(state));
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    } catch (error) {
      console.warn('[Le Souvenir Simple] Sauvegarde du configurateur impossible.', error);
    }
    return normalized;
  }

  function getKind(kindId) {
    return CATALOGUE && CATALOGUE.kinds ? CATALOGUE.kinds[kindId] : null;
  }

  function getMaterial(kindId, materialId) {
    const kind = getKind(kindId);
    return kind && kind.materials ? kind.materials[materialId] : null;
  }

  function getSize(kindId, materialId, sizeId) {
    const material = getMaterial(kindId, materialId);
    if (!material || !Array.isArray(material.sizes)) return null;
    return material.sizes.find(function (entry) { return entry.id === sizeId; }) || null;
  }

  function getMotif(kindId, materialId, motifId) {
    const material = getMaterial(kindId, materialId);
    if (!material || !Array.isArray(material.motifs)) return null;
    return material.motifs.find(function (entry) { return entry.id === motifId; }) || null;
  }

  function getPalette(materialId) {
    if (!CATALOGUE || !CATALOGUE.palettes) return [];
    const key = materialId === 'wood' ? 'wood' : 'silicone';
    return (CATALOGUE.palettes[key] || []).filter(function (color) { return color.active !== false; });
  }

  function getColor(materialId, colorId) {
    return getPalette(materialId).find(function (entry) { return entry.id === colorId; }) || null;
  }

  function pieceLabel(piece) {
    const kind = getKind(piece.kind);
    const material = getMaterial(piece.kind, piece.material);
    const size = piece.sizeId ? getSize(piece.kind, piece.material, piece.sizeId) : null;
    const motif = piece.motifId ? getMotif(piece.kind, piece.material, piece.motifId) : null;
    const bits = [motif ? motif.label : (kind ? kind.label : 'Élément')];
    if (material) bits.push(material.label);
    if (size) bits.push(size.label);
    if (piece.colorName) bits.push(piece.colorName);
    return bits.join(' · ');
  }

  function pieceVisualSize(piece) {
    const size = piece.sizeId ? getSize(piece.kind, piece.material, piece.sizeId) : null;
    const motif = piece.motifId ? getMotif(piece.kind, piece.material, piece.motifId) : null;
    const nominal = size && Number(size.diameterMm)
      ? Number(size.diameterMm)
      : motif && Number(motif.widthMm)
        ? Number(motif.widthMm)
        : 12;
    return Math.max(34, Math.min(62, Math.round(nominal * 3.1)));
  }

  function createCharacterElement(character, compact) {
    const element = document.createElement('span');
    element.className = 'lss-vs-character' + (compact ? ' lss-vs-character--compact' : '');
    element.setAttribute('aria-hidden', 'true');

    if (isWhitespace(character)) {
      element.classList.add('lss-vs-character--space');
      return element;
    }
    if (isPunctuation(character)) {
      element.classList.add('lss-vs-character--punctuation');
      element.textContent = character;
      return element;
    }
    if (isParticularCharacter(character)) element.classList.add('lss-vs-character--special');
    element.textContent = character;
    return element;
  }

  function renderSimpleName(container, workingName) {
    container.innerHTML = '';
    nameCharacters(workingName).forEach(function (character) {
      container.appendChild(createCharacterElement(character, false));
    });
  }

  function createPieceElement(piece, selectedPieceId, interactive) {
    const element = document.createElement(interactive ? 'button' : 'span');
    if (interactive) element.type = 'button';
    element.className = 'lss-vs-piece lss-vs-piece--' + piece.kind;
    element.style.setProperty('--lss-piece-color', piece.colorHex || '#EFE5D8');
    element.style.setProperty('--lss-piece-size', pieceVisualSize(piece) + 'px');
    element.title = pieceLabel(piece);

    if (interactive) {
      element.dataset.lssVsPieceId = piece.id;
      element.draggable = true;
      element.setAttribute('aria-label', pieceLabel(piece) + '. Sélectionner ou déplacer.');
      if (piece.id === selectedPieceId) element.classList.add('is-selected');
    } else {
      element.setAttribute('aria-hidden', 'true');
    }

    if (piece.kind === 'motif') {
      const motif = getMotif(piece.kind, piece.material, piece.motifId);
      element.textContent = motif ? motif.symbol : '✦';
    } else if (interactive) {
      const hidden = document.createElement('span');
      hidden.className = 'visually-hidden';
      hidden.textContent = pieceLabel(piece);
      element.appendChild(hidden);
    }

    return element;
  }

  function createInsertMarker(slotIndex, insertIndex) {
    const marker = document.createElement('button');
    marker.type = 'button';
    marker.className = 'lss-vs-insert-marker';
    marker.dataset.lssVsSlotIndex = String(slotIndex);
    marker.dataset.lssVsInsertIndex = String(insertIndex);
    marker.textContent = '+';
    marker.setAttribute('aria-label', 'Déposer l’élément ici');
    return marker;
  }

  function locatePiece(state, pieceId) {
    const slots = state.composition.slots;
    for (let slotIndex = 0; slotIndex < slots.length; slotIndex += 1) {
      const pieceIndex = slots[slotIndex].findIndex(function (piece) { return piece.id === pieceId; });
      if (pieceIndex !== -1) {
        return { slotIndex: slotIndex, pieceIndex: pieceIndex, piece: slots[slotIndex][pieceIndex] };
      }
    }
    return null;
  }

  function countPieces(state) {
    return state.composition.slots.reduce(function (sum, slot) { return sum + slot.length; }, 0);
  }

  function safeText(value, max) {
    return String(value || '').replace(/[\r\n\t]+/g, ' ').trim().slice(0, max || 1000);
  }

  function initConfigurator(section) {
    if (!section || section.dataset.lssVsInitialized === 'true') return;
    section.dataset.lssVsInitialized = 'true';

    if (!CATALOGUE || !RULES) {
      console.error('[Le Souvenir Simple] Catalogue ou moteur de règles introuvable.');
      return;
    }

    const steps = {};
    section.querySelectorAll('[data-lss-vs-step]').forEach(function (node) {
      steps[Number(node.dataset.lssVsStep)] = node;
    });

    const input = section.querySelector('[data-lss-vs-prenom]');
    const preview = section.querySelector('[data-lss-vs-preview]');
    const specialNote = section.querySelector('[data-lss-vs-special-note]');
    const startMessage = section.querySelector('[data-lss-vs-start-message]');
    const continueNameButton = section.querySelector('[data-lss-vs-continue-name]');
    const editNameButton = section.querySelector('[data-lss-vs-edit-name]');

    const sequence = section.querySelector('[data-lss-vs-sequence]');
    const compositionStatus = section.querySelector('[data-lss-vs-composition-status]');
    const selectedPanel = section.querySelector('[data-lss-vs-selected-panel]');
    const selectedLabel = section.querySelector('[data-lss-vs-selected-label]');
    const editPieceButton = section.querySelector('[data-lss-vs-edit-piece]');
    const duplicatePieceButton = section.querySelector('[data-lss-vs-duplicate-piece]');
    const repositionPieceButton = section.querySelector('[data-lss-vs-reposition-piece]');
    const deletePieceButton = section.querySelector('[data-lss-vs-delete-piece]');
    const continueCompositionButton = section.querySelector('[data-lss-vs-continue-composition]');
    const openPickerButtons = section.querySelectorAll('[data-lss-vs-open-picker]');

    const balance = section.querySelector('[data-lss-vs-balance]');
    const balanceState = section.querySelector('[data-lss-vs-balance-state]');
    const balanceFill = section.querySelector('[data-lss-vs-balance-fill]');
    const balanceText = section.querySelector('[data-lss-vs-balance-text]');
    const technicalDetailsText = section.querySelector('[data-lss-vs-technical-details-text]');

    const inspirationModeButtons = section.querySelectorAll('[data-lss-vs-inspiration-mode]');
    const inspirationPresetsPanel = section.querySelector('[data-lss-vs-inspiration-presets]');
    const inspirationIdeaPanel = section.querySelector('[data-lss-vs-inspiration-idea]');
    const presetList = section.querySelector('[data-lss-vs-preset-list]');
    const ideaInput = section.querySelector('[data-lss-vs-idea]');
    const ideaSuggestButton = section.querySelector('[data-lss-vs-idea-suggest]');
    const ideaResults = section.querySelector('[data-lss-vs-idea-results]');
    const continueInspirationButton = section.querySelector('[data-lss-vs-continue-inspiration]');
    const backCompositionButtons = section.querySelectorAll('[data-lss-vs-back-composition]');

    const accompanimentRadios = section.querySelectorAll('[data-lss-vs-accompagnement]');
    const symbolicWrap = section.querySelector('[data-lss-vs-symbolic-wrap]');
    const symbolicInput = section.querySelector('[data-lss-vs-symbolic]');
    const continueStoryButton = section.querySelector('[data-lss-vs-continue-story]');
    const backInspirationButton = section.querySelector('[data-lss-vs-back-inspiration]');

    const summarySequence = section.querySelector('[data-lss-vs-summary-sequence]');
    const summaryName = section.querySelector('[data-lss-vs-summary-name]');
    const summaryComposition = section.querySelector('[data-lss-vs-summary-composition]');
    const summaryInspiration = section.querySelector('[data-lss-vs-summary-inspiration]');
    const summaryAccompagnement = section.querySelector('[data-lss-vs-summary-accompagnement]');
    const summaryTechnical = section.querySelector('[data-lss-vs-summary-technical]');
    const finalAccept = section.querySelector('[data-lss-vs-final-accept]');
    const addToCartButton = section.querySelector('[data-lss-vs-add-to-cart]');
    const backStoryButton = section.querySelector('[data-lss-vs-back-story]');
    const productForm = section.querySelector('.lss-vs-product-form');
    const propName = section.querySelector('[data-lss-vs-prop-name]');
    const propConfigId = section.querySelector('[data-lss-vs-prop-config-id]');
    const propComposition = section.querySelector('[data-lss-vs-prop-composition]');
    const propInspiration = section.querySelector('[data-lss-vs-prop-inspiration]');
    const propAccompagnement = section.querySelector('[data-lss-vs-prop-accompagnement]');
    const propSymbolic = section.querySelector('[data-lss-vs-prop-symbolic]');

    const modal = section.querySelector('[data-lss-vs-picker-modal]');
    const modalClose = section.querySelector('[data-lss-vs-modal-close]');
    const modalTitle = section.querySelector('[data-lss-vs-modal-title]');
    const modalNote = section.querySelector('[data-lss-vs-modal-note]');
    const materialSelect = section.querySelector('[data-lss-vs-material]');
    const sizeWrap = section.querySelector('[data-lss-vs-size-wrap]');
    const sizeSelect = section.querySelector('[data-lss-vs-size]');
    const motifWrap = section.querySelector('[data-lss-vs-motif-wrap]');
    const motifSelect = section.querySelector('[data-lss-vs-motif]');
    const colorsContainer = section.querySelector('[data-lss-vs-colors]');
    const modalPreview = section.querySelector('[data-lss-vs-modal-preview]');
    const modalSubmit = section.querySelector('[data-lss-vs-modal-submit]');
    const toast = section.querySelector('[data-lss-vs-toast]');

    if (!input || !preview || !sequence || !modal || !materialSelect || !colorsContainer) {
      console.error('[Le Souvenir Simple] Structure du configurateur incomplète.');
      return;
    }

    let state = getStoredState();
    let selectedPieceId = null;
    let placementMode = false;
    let draggedPieceId = null;
    let pickerKind = null;
    let editingPieceId = null;
    let selectedColorId = null;
    let toastTimer = null;

    function showToast(message) {
      if (!toast) return;
      toast.textContent = message;
      toast.hidden = false;
      window.clearTimeout(toastTimer);
      toastTimer = window.setTimeout(function () { toast.hidden = true; }, 2800);
    }

    function currentTechnicalReport() {
      return RULES.evaluate(state, CATALOGUE);
    }

    function showStep(number, shouldScroll) {
      Object.keys(steps).forEach(function (key) {
        steps[key].hidden = Number(key) !== number;
      });
      state.currentStep = number;
      state = saveState(state);

      if (number === 3) renderInspiration();
      if (number === 4) renderStory();
      if (number === 5) renderSummary();

      if (shouldScroll && steps[number]) {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        steps[number].scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      }
    }

    function updateName(originalValue, shouldSave) {
      const workingValue = createWorkingName(originalValue);
      const previous = state.prenomTravail;
      state.prenomOriginal = originalValue;
      state.prenomTravail = workingValue;

      if (previous !== workingValue) state = ensureSlotCount(state);

      renderSimpleName(preview, workingValue);

      const characters = nameCharacters(workingValue);
      const hasParticular = characters.some(isParticularCharacter);
      const hasName = workingValue.length > 0;

      specialNote.hidden = !hasParticular;
      startMessage.hidden = !hasName;
      continueNameButton.disabled = !hasName;
      continueNameButton.setAttribute('aria-disabled', hasName ? 'false' : 'true');
      preview.setAttribute('aria-label', hasName ? 'Aperçu du prénom : ' + workingValue : 'Aucun prénom saisi');

      if (shouldSave) state = saveState(state);
      renderComposition();
    }

    function renderSlot(slot, slotIndex, target, interactive) {
      const wrapper = document.createElement('span');
      wrapper.className = 'lss-vs-slot';
      wrapper.dataset.lssVsSlot = String(slotIndex);

      if (interactive) wrapper.appendChild(createInsertMarker(slotIndex, 0));

      slot.forEach(function (piece, pieceIndex) {
        wrapper.appendChild(createPieceElement(piece, selectedPieceId, interactive));
        if (interactive) wrapper.appendChild(createInsertMarker(slotIndex, pieceIndex + 1));
      });

      target.appendChild(wrapper);
    }

    function renderSequence(target, interactive) {
      if (!target) return;
      target.innerHTML = '';
      if (interactive) target.classList.toggle('is-placement-mode', placementMode && Boolean(selectedPieceId));

      const chars = nameCharacters(state.prenomTravail);
      state.composition.slots.forEach(function (slot, slotIndex) {
        renderSlot(slot, slotIndex, target, interactive);
        if (slotIndex < chars.length) target.appendChild(createCharacterElement(chars[slotIndex], true));
      });
    }

    function updateBalance() {
      const report = currentTechnicalReport();
      if (!balance) return report;

      balance.dataset.level = report.level;
      balanceFill.style.width = report.percent + '%';
      balanceState.textContent = report.level === 'blocked'
        ? 'À corriger'
        : report.level === 'high'
          ? 'Très dense'
          : report.level === 'warning'
            ? 'Dense'
            : report.level === 'check'
              ? 'À confirmer'
              : 'Serein';
      balanceText.textContent = report.status;

      const details = [];
      details.push('Composants actuellement mesurables : ' + report.knownLengthMm + ' mm.');
      details.push('Repère maximal sans dispositif de fixation au vêtement : ' + report.limitMm + ' mm.');
      if (report.unknownComponents > 0) details.push(report.unknownComponents + ' composant(s) restent à mesurer précisément.');
      if (report.warnings.length) details.push(report.warnings.join(' '));
      details.push('Ce repère ne constitue pas une déclaration de conformité du produit fini.');
      technicalDetailsText.textContent = details.join(' ');

      return report;
    }

    function renderSelectedPanel() {
      if (!selectedPanel) return;
      const location = selectedPieceId ? locatePiece(state, selectedPieceId) : null;
      if (!location) {
        selectedPieceId = null;
        placementMode = false;
        selectedPanel.hidden = true;
        return;
      }
      selectedPanel.hidden = false;
      selectedLabel.textContent = pieceLabel(location.piece);
    }

    function renderComposition() {
      state = ensureSlotCount(state);
      renderSequence(sequence, true);

      const total = countPieces(state);
      compositionStatus.textContent = total === 0
        ? 'Votre prénom attend les premiers éléments de sa composition.'
        : total === 1
          ? '1 élément accompagne maintenant le prénom.'
          : total + ' éléments composent maintenant votre Souvenir.';

      const report = updateBalance();
      continueCompositionButton.disabled = total === 0 || !report.canContinue;
      continueCompositionButton.setAttribute('aria-disabled', continueCompositionButton.disabled ? 'true' : 'false');
      renderSelectedPanel();
    }

    function movePieceTo(pieceId, targetSlotIndex, targetInsertIndex) {
      const source = locatePiece(state, pieceId);
      if (!source) return;

      const sourceSlot = state.composition.slots[source.slotIndex];
      const piece = sourceSlot.splice(source.pieceIndex, 1)[0];

      let insertIndex = targetInsertIndex;
      if (source.slotIndex === targetSlotIndex && source.pieceIndex < targetInsertIndex) insertIndex -= 1;

      const targetSlot = state.composition.slots[targetSlotIndex];
      insertIndex = Math.max(0, Math.min(insertIndex, targetSlot.length));
      targetSlot.splice(insertIndex, 0, piece);

      selectedPieceId = pieceId;
      placementMode = false;
      state = saveState(state);
      renderComposition();
      showToast('Élément repositionné.');
    }

    function removePiece(pieceId) {
      const location = locatePiece(state, pieceId);
      if (!location) return;
      state.composition.slots[location.slotIndex].splice(location.pieceIndex, 1);
      selectedPieceId = null;
      placementMode = false;
      state = saveState(state);
      renderComposition();
      showToast('Élément supprimé.');
    }

    function duplicatePiece(pieceId) {
      const location = locatePiece(state, pieceId);
      if (!location) return;
      const copy = { ...location.piece, id: createPieceId() };
      const permission = RULES.canAddPiece(state, copy, CATALOGUE);
      if (!permission.allowed) {
        showToast(permission.reason || 'Cet ajout n’est pas possible dans cette composition.');
        return;
      }
      state.composition.slots[location.slotIndex].splice(location.pieceIndex + 1, 0, copy);
      selectedPieceId = copy.id;
      placementMode = true;
      state = saveState(state);
      renderComposition();
      showToast('Élément dupliqué. Choisissez sa place.');
    }

    function defaultColorForMaterial(materialId) {
      return getPalette(materialId)[0] || null;
    }

    function fillMaterialOptions(kindId, preferredMaterial) {
      const kind = getKind(kindId);
      materialSelect.innerHTML = '';
      const materials = Object.entries((kind && kind.materials) || {}).filter(function (entry) {
        return entry[1] && entry[1].active !== false;
      });

      materials.forEach(function (entry) {
        const option = document.createElement('option');
        option.value = entry[0];
        option.textContent = entry[1].label;
        materialSelect.appendChild(option);
      });

      if (preferredMaterial && materials.some(function (entry) { return entry[0] === preferredMaterial; })) {
        materialSelect.value = preferredMaterial;
      }
    }

    function fillSizeOptions(preferredSizeId) {
      const material = getMaterial(pickerKind, materialSelect.value);
      const sizes = material && Array.isArray(material.sizes)
        ? material.sizes.filter(function (size) { return size.active !== false; })
        : [];

      sizeSelect.innerHTML = '';
      sizeWrap.hidden = sizes.length === 0;

      sizes.forEach(function (size) {
        const option = document.createElement('option');
        option.value = size.id;
        option.textContent = size.label;
        sizeSelect.appendChild(option);
      });

      const desired = preferredSizeId || (material && material.defaultSizeId);
      if (desired && sizes.some(function (size) { return size.id === desired; })) sizeSelect.value = desired;
    }

    function fillMotifOptions(preferredMotifId) {
      const material = getMaterial(pickerKind, materialSelect.value);
      const motifs = material && Array.isArray(material.motifs)
        ? material.motifs.filter(function (motif) { return motif.active !== false; })
        : [];

      motifSelect.innerHTML = '';
      motifWrap.hidden = motifs.length === 0;

      motifs.forEach(function (motif) {
        const option = document.createElement('option');
        option.value = motif.id;
        option.textContent = motif.label;
        motifSelect.appendChild(option);
      });

      const desired = preferredMotifId || (material && material.defaultMotifId);
      if (desired && motifs.some(function (motif) { return motif.id === desired; })) motifSelect.value = desired;
    }

    function activePaletteForPicker() {
      let palette = getPalette(materialSelect.value);
      if (pickerKind === 'motif' && !motifWrap.hidden) {
        const motif = getMotif(pickerKind, materialSelect.value, motifSelect.value);
        if (motif && Array.isArray(motif.preferredColorIds) && motif.preferredColorIds.length) {
          const filtered = palette.filter(function (color) { return motif.preferredColorIds.includes(color.id); });
          if (filtered.length) palette = filtered;
        }
      }
      return palette;
    }

    function fillColorOptions(preferredColorId) {
      const palette = activePaletteForPicker();
      colorsContainer.innerHTML = '';

      const preferred = palette.find(function (color) { return color.id === preferredColorId; }) || palette[0] || null;
      selectedColorId = preferred ? preferred.id : null;

      palette.forEach(function (color) {
        const label = document.createElement('label');
        label.className = 'lss-vs-color-choice';

        const radio = document.createElement('input');
        radio.type = 'radio';
        radio.name = 'lss-vs-color';
        radio.value = color.id;
        radio.checked = color.id === selectedColorId;
        radio.dataset.lssVsColorChoice = color.id;

        const dot = document.createElement('span');
        dot.className = 'lss-vs-color-dot';
        dot.style.setProperty('--lss-swatch-color', color.hex);
        dot.setAttribute('aria-hidden', 'true');

        const name = document.createElement('span');
        name.className = 'lss-vs-color-name';
        name.textContent = color.name;

        label.appendChild(radio);
        label.appendChild(dot);
        label.appendChild(name);
        colorsContainer.appendChild(label);
      });
    }

    function currentPickerPiece() {
      if (!pickerKind) return null;
      const materialId = materialSelect.value;
      const color = getColor(materialId, selectedColorId) || defaultColorForMaterial(materialId);
      const size = sizeWrap.hidden ? null : getSize(pickerKind, materialId, sizeSelect.value);
      const motif = motifWrap.hidden ? null : getMotif(pickerKind, materialId, motifSelect.value);
      const supplierConfirmed = CATALOGUE.supplier && CATALOGUE.supplier.status === 'confirmed';

      return {
        id: editingPieceId || createPieceId(),
        kind: pickerKind,
        material: materialId,
        sizeId: size ? size.id : null,
        motifId: motif ? motif.id : null,
        colorId: color ? color.id : null,
        colorName: color ? color.name : '',
        colorHex: color ? color.hex : '#EFE5D8',
        catalogueConfirmed: supplierConfirmed && Boolean((size && size.supplierObserved) || (motif && motif.supplierObserved))
      };
    }

    function updateModalPreview() {
      const piece = currentPickerPiece();
      if (!piece) return;
      modalPreview.innerHTML = '';
      const visual = createPieceElement(piece, null, false);
      modalPreview.appendChild(visual);

      const text = document.createElement('p');
      text.className = 'lss-vs-modal-preview-label';
      text.textContent = pieceLabel(piece);
      modalPreview.appendChild(text);
    }

    function refreshPicker(preferred) {
      fillSizeOptions(preferred && preferred.sizeId);
      fillMotifOptions(preferred && preferred.motifId);
      fillColorOptions(preferred && preferred.colorId);
      updateModalPreview();
    }

    function openPicker(kindId, pieceId) {
      const kind = getKind(kindId);
      if (!kind) return;

      pickerKind = kindId;
      editingPieceId = pieceId || null;
      const existingLocation = pieceId ? locatePiece(state, pieceId) : null;
      const existing = existingLocation ? existingLocation.piece : null;

      modalTitle.textContent = existing ? 'Modifier ' + kind.label.toLowerCase() : kind.label;
      modalSubmit.textContent = existing ? 'ENREGISTRER LES MODIFICATIONS' : 'AJOUTER À MA CRÉATION';
      modalNote.textContent = CATALOGUE.ui && CATALOGUE.ui.swatchDisclaimer
        ? CATALOGUE.ui.swatchDisclaimer
        : '';
      modalNote.hidden = !modalNote.textContent;
      fillMaterialOptions(kindId, existing && existing.material);
      refreshPicker(existing);

      if (typeof modal.showModal === 'function') modal.showModal();
      else modal.setAttribute('open', '');
    }

    function closePicker() {
      if (typeof modal.close === 'function') modal.close();
      else modal.removeAttribute('open');
      pickerKind = null;
      editingPieceId = null;
    }

    function savePickerPiece() {
      const piece = currentPickerPiece();
      if (!piece) return;

      if (editingPieceId) {
        const location = locatePiece(state, editingPieceId);
        if (!location) return;
        const old = state.composition.slots[location.slotIndex][location.pieceIndex];
        state.composition.slots[location.slotIndex][location.pieceIndex] = piece;
        const report = currentTechnicalReport();
        if (!report.canContinue) {
          state.composition.slots[location.slotIndex][location.pieceIndex] = old;
          showToast(report.blockingReasons[0] || 'Cette modification n’est pas possible.');
          return;
        }
        selectedPieceId = piece.id;
        placementMode = false;
        showToast('Élément modifié.');
      } else {
        const permission = RULES.canAddPiece(state, piece, CATALOGUE);
        if (!permission.allowed) {
          showToast(permission.reason || 'Cet ajout n’est pas possible dans cette composition.');
          return;
        }
        state.composition.slots[state.composition.slots.length - 1].push(piece);
        selectedPieceId = piece.id;
        placementMode = true;
        showToast('Élément ajouté. Touchez un + ou faites-le glisser pour le positionner.');
      }

      state = saveState(state);
      closePicker();
      renderComposition();
    }

    function renderPresetList() {
      presetList.innerHTML = '';
      (CATALOGUE.inspirationPresets || []).forEach(function (preset) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'lss-vs-preset-card';
        button.dataset.lssVsPresetId = preset.id;
        if (state.inspiration.presetId === preset.id) button.classList.add('is-selected');

        const title = document.createElement('strong');
        title.textContent = preset.title;
        const desc = document.createElement('span');
        desc.textContent = preset.description;
        const swatches = document.createElement('span');
        swatches.className = 'lss-vs-preset-swatches';

        preset.colorIds.forEach(function (colorId) {
          let color = getColor('silicone', colorId) || getColor('wood', colorId);
          if (!color) return;
          const dot = document.createElement('i');
          dot.style.setProperty('--lss-swatch-color', color.hex);
          dot.title = color.name;
          swatches.appendChild(dot);
        });

        button.appendChild(title);
        button.appendChild(desc);
        button.appendChild(swatches);
        presetList.appendChild(button);
      });
    }

    function ideaSuggestionColors(text) {
      const lower = String(text || '').toLocaleLowerCase('fr-FR');
      const map = [
        { words: ['beige', 'coco', 'crème', 'creme', 'ivoire'], ids: ['coco', 'galet'] },
        { words: ['rose', 'pêche', 'peche'], ids: ['rose-doux', 'bois-de-rose', 'nude-peche'] },
        { words: ['vert', 'sauge', 'pistache'], ids: ['pistache', 'menthe-clair'] },
        { words: ['bleu', 'ciel'], ids: ['bleu-poudre-clair'] },
        { words: ['lilas', 'violet', 'mauve'], ids: ['lilas-poudre'] },
        { words: ['taupe', 'brun', 'marron'], ids: ['taupe', 'brun-faon'] },
        { words: ['nature', 'bois', 'naturel'], ids: ['hetre-naturel'] }
      ];

      const ids = [];
      map.forEach(function (entry) {
        if (entry.words.some(function (word) { return lower.includes(word); })) {
          entry.ids.forEach(function (id) { if (!ids.includes(id)) ids.push(id); });
        }
      });
      if (!ids.length) ids.push('coco', 'pistache', 'rose-doux');
      return ids.slice(0, 4);
    }

    function renderIdeaResults() {
      const text = safeText(ideaInput.value, 180);
      if (!text) {
        ideaResults.hidden = true;
        ideaResults.innerHTML = '';
        return;
      }

      const ids = ideaSuggestionColors(text);
      ideaResults.innerHTML = '';

      const paragraph = document.createElement('p');
      paragraph.textContent = 'L’Atelier retient cette direction :';
      ideaResults.appendChild(paragraph);

      const chips = document.createElement('div');
      chips.className = 'lss-vs-idea-chips';
      ids.forEach(function (id) {
        const color = getColor('silicone', id) || getColor('wood', id);
        if (!color) return;
        const chip = document.createElement('span');
        const dot = document.createElement('i');
        dot.style.setProperty('--lss-swatch-color', color.hex);
        chip.appendChild(dot);
        chip.appendChild(document.createTextNode(color.name));
        chips.appendChild(chip);
      });
      ideaResults.appendChild(chips);
      ideaResults.hidden = false;
    }

    function updateInspirationContinue() {
      let valid = false;
      if (state.inspiration.mode === 'solo') valid = true;
      if (state.inspiration.mode === 'preset') valid = Boolean(state.inspiration.presetId);
      if (state.inspiration.mode === 'idea') valid = Boolean(safeText(state.inspiration.ideaText, 180));
      continueInspirationButton.disabled = !valid;
      continueInspirationButton.setAttribute('aria-disabled', valid ? 'false' : 'true');
    }

    function renderInspiration() {
      inspirationModeButtons.forEach(function (button) {
        button.classList.toggle('is-selected', button.dataset.lssVsInspirationMode === state.inspiration.mode);
      });

      inspirationPresetsPanel.hidden = state.inspiration.mode !== 'preset';
      inspirationIdeaPanel.hidden = state.inspiration.mode !== 'idea';
      ideaInput.value = state.inspiration.ideaText;
      renderPresetList();
      if (state.inspiration.ideaText) renderIdeaResults();
      else ideaResults.hidden = true;
      updateInspirationContinue();
    }

    function renderStory() {
      accompanimentRadios.forEach(function (radio) {
        radio.checked = state.accompagnement.wanted === (radio.value === 'oui');
      });
      symbolicWrap.hidden = state.accompagnement.wanted !== true;
      symbolicInput.value = state.accompagnement.symbolic;
      const valid = state.accompagnement.wanted !== null;
      continueStoryButton.disabled = !valid;
      continueStoryButton.setAttribute('aria-disabled', valid ? 'false' : 'true');
    }

    function inspirationLabel() {
      if (state.inspiration.mode === 'solo') return 'Composition libre, sans piste supplémentaire';
      if (state.inspiration.mode === 'preset') {
        const preset = (CATALOGUE.inspirationPresets || []).find(function (item) { return item.id === state.inspiration.presetId; });
        return preset ? preset.title : 'Inspiration de la Maison';
      }
      if (state.inspiration.mode === 'idea') return state.inspiration.ideaText ? 'Idée : ' + state.inspiration.ideaText : 'Idée personnelle';
      return '—';
    }

    function renderSummary() {
      renderSequence(summarySequence, false);
      const report = currentTechnicalReport();
      const compositionText = RULES.compactComposition(state, CATALOGUE);

      summaryName.textContent = state.prenomOriginal.trim() || state.prenomTravail;
      summaryComposition.textContent = compositionText || 'Aucun élément ajouté';
      summaryInspiration.textContent = inspirationLabel();
      summaryAccompagnement.textContent = state.accompagnement.wanted
        ? 'Oui' + (state.accompagnement.symbolic ? ' — ' + state.accompagnement.symbolic : '')
        : 'Non';
      summaryTechnical.textContent = report.canContinue
        ? (report.unknownComponents > 0 ? 'Contrôle atelier requis' : 'Repère de conception cohérent')
        : 'Composition à corriger';

      propName.value = safeText(state.prenomOriginal.trim() || state.prenomTravail, 120);
      propConfigId.value = state.configId;
      propComposition.value = safeText(compositionText, 1200);
      propInspiration.value = safeText(inspirationLabel(), 240);
      propAccompagnement.value = state.accompagnement.wanted ? 'Oui' : 'Non';
      propSymbolic.value = safeText(state.accompagnement.symbolic, 80);

      updateFinalButton();
    }

    function updateFinalButton() {
      const report = currentTechnicalReport();
      const productAvailable = section.dataset.productAvailable === 'true';
      const enabled = Boolean(finalAccept.checked) && report.canContinue && productAvailable;
      addToCartButton.disabled = !enabled;
      addToCartButton.setAttribute('aria-disabled', enabled ? 'false' : 'true');
    }

    input.addEventListener('input', function (event) {
      updateName(event.target.value, true);
    });

    continueNameButton.addEventListener('click', function () {
      if (!createWorkingName(input.value)) {
        input.focus();
        return;
      }
      updateName(input.value, true);
      showStep(2, true);
    });

    editNameButton.addEventListener('click', function () {
      showStep(1, true);
      window.setTimeout(function () { input.focus(); }, 250);
    });

    openPickerButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        openPicker(button.dataset.lssVsOpenPicker, null);
      });
    });

    materialSelect.addEventListener('change', function () { refreshPicker(null); });
    sizeSelect.addEventListener('change', updateModalPreview);
    motifSelect.addEventListener('change', function () {
      fillColorOptions(null);
      updateModalPreview();
    });

    colorsContainer.addEventListener('change', function (event) {
      const target = event.target.closest('[data-lss-vs-color-choice]');
      if (!target) return;
      selectedColorId = target.value;
      updateModalPreview();
    });

    modalSubmit.addEventListener('click', savePickerPiece);
    modalClose.addEventListener('click', closePicker);
    modal.addEventListener('click', function (event) { if (event.target === modal) closePicker(); });

    sequence.addEventListener('click', function (event) {
      const pieceButton = event.target.closest('[data-lss-vs-piece-id]');
      if (pieceButton) {
        selectedPieceId = selectedPieceId === pieceButton.dataset.lssVsPieceId ? null : pieceButton.dataset.lssVsPieceId;
        placementMode = Boolean(selectedPieceId);
        renderComposition();
        return;
      }

      const marker = event.target.closest('[data-lss-vs-slot-index]');
      if (marker && selectedPieceId) {
        movePieceTo(selectedPieceId, Number(marker.dataset.lssVsSlotIndex), Number(marker.dataset.lssVsInsertIndex));
      }
    });

    sequence.addEventListener('dragstart', function (event) {
      const pieceButton = event.target.closest('[data-lss-vs-piece-id]');
      if (!pieceButton) return;
      draggedPieceId = pieceButton.dataset.lssVsPieceId;
      selectedPieceId = draggedPieceId;
      placementMode = true;
      sequence.classList.add('is-placement-mode');
      pieceButton.classList.add('is-dragging');
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', draggedPieceId);
      }
    });

    sequence.addEventListener('dragover', function (event) {
      const marker = event.target.closest('[data-lss-vs-slot-index]');
      if (!marker || !draggedPieceId) return;
      event.preventDefault();
      marker.classList.add('is-drag-over');
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    });

    sequence.addEventListener('dragleave', function (event) {
      const marker = event.target.closest('[data-lss-vs-slot-index]');
      if (marker) marker.classList.remove('is-drag-over');
    });

    sequence.addEventListener('drop', function (event) {
      const marker = event.target.closest('[data-lss-vs-slot-index]');
      if (!marker || !draggedPieceId) return;
      event.preventDefault();
      movePieceTo(draggedPieceId, Number(marker.dataset.lssVsSlotIndex), Number(marker.dataset.lssVsInsertIndex));
    });

    sequence.addEventListener('dragend', function () {
      draggedPieceId = null;
      placementMode = Boolean(selectedPieceId);
      renderComposition();
    });

    editPieceButton.addEventListener('click', function () {
      const location = selectedPieceId ? locatePiece(state, selectedPieceId) : null;
      if (location) openPicker(location.piece.kind, location.piece.id);
    });

    duplicatePieceButton.addEventListener('click', function () {
      if (selectedPieceId) duplicatePiece(selectedPieceId);
    });

    repositionPieceButton.addEventListener('click', function () {
      if (!selectedPieceId) return;
      placementMode = true;
      renderComposition();
      showToast('Choisissez un + entre les lettres, ou faites glisser l’élément.');
    });

    deletePieceButton.addEventListener('click', function () {
      if (selectedPieceId) removePiece(selectedPieceId);
    });

    continueCompositionButton.addEventListener('click', function () {
      const report = currentTechnicalReport();
      if (countPieces(state) === 0 || !report.canContinue) return;
      showStep(3, true);
    });

    backCompositionButtons.forEach(function (button) {
      button.addEventListener('click', function () { showStep(2, true); });
    });

    inspirationModeButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        const mode = button.dataset.lssVsInspirationMode;
        state.inspiration.mode = mode;
        if (mode !== 'preset') state.inspiration.presetId = '';
        state = saveState(state);
        renderInspiration();
      });
    });

    presetList.addEventListener('click', function (event) {
      const card = event.target.closest('[data-lss-vs-preset-id]');
      if (!card) return;
      state.inspiration.mode = 'preset';
      state.inspiration.presetId = card.dataset.lssVsPresetId;
      state = saveState(state);
      renderInspiration();
    });

    ideaInput.addEventListener('input', function () {
      state.inspiration.mode = 'idea';
      state.inspiration.ideaText = safeText(ideaInput.value, 180);
      state = saveState(state);
      updateInspirationContinue();
    });

    ideaSuggestButton.addEventListener('click', function () {
      state.inspiration.ideaText = safeText(ideaInput.value, 180);
      state = saveState(state);
      renderIdeaResults();
      updateInspirationContinue();
    });

    continueInspirationButton.addEventListener('click', function () {
      if (continueInspirationButton.disabled) return;
      showStep(4, true);
    });

    accompanimentRadios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        state.accompagnement.wanted = radio.value === 'oui';
        if (!state.accompagnement.wanted) state.accompagnement.symbolic = '';
        state = saveState(state);
        renderStory();
      });
    });

    symbolicInput.addEventListener('input', function () {
      state.accompagnement.symbolic = safeText(symbolicInput.value, 80);
      state = saveState(state);
    });

    continueStoryButton.addEventListener('click', function () {
      if (continueStoryButton.disabled) return;
      state.accompagnement.symbolic = safeText(symbolicInput.value, 80);
      state = saveState(state);
      showStep(5, true);
    });

    backInspirationButton.addEventListener('click', function () { showStep(3, true); });
    backStoryButton.addEventListener('click', function () { showStep(4, true); });

    finalAccept.addEventListener('change', updateFinalButton);

    if (productForm) {
      productForm.addEventListener('submit', function (event) {
        renderSummary();
        const report = currentTechnicalReport();
        if (!finalAccept.checked || !report.canContinue || section.dataset.productAvailable !== 'true') {
          event.preventDefault();
          showToast('Vérifiez la validation finale avant d’ajouter votre Souvenir au panier.');
        }
      });
    }

    input.value = state.prenomOriginal;
    ideaInput.value = state.inspiration.ideaText;
    symbolicInput.value = state.accompagnement.symbolic;
    updateName(state.prenomOriginal, false);
    renderComposition();

    if (!state.prenomTravail) {
      state.currentStep = 1;
      state = saveState(state);
    }

    if (state.currentStep > 2 && countPieces(state) === 0) {
      state.currentStep = 2;
      state = saveState(state);
    }

    showStep(state.currentStep, false);
  }

  function initAll(root) {
    const scope = root || document;
    if (scope.matches && scope.matches('[data-lss-vs-config]')) initConfigurator(scope);
    if (scope.querySelectorAll) scope.querySelectorAll('[data-lss-vs-config]').forEach(initConfigurator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();